import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

void React;

export interface HostCompanyLogoSessionValue {
  /** Bumped after a successful host logo upload so preview re-resolves branding. */
  refreshToken: number;
  /** Data-URI preview between file pick and host branding resolution. */
  sessionPreviewUrl: string | null;
  setSessionPreviewUrl: (url: string | null) => void;
  /**
   * When true, skip host `resolveCompanyLogoUrl` so a user clear stays cleared in-editor
   * (company branding may still have a logo until the host exposes a delete API).
   */
  suppressHostLogo: boolean;
  setSuppressHostLogo: (suppress: boolean) => void;
}

const defaultSession: HostCompanyLogoSessionValue = {
  refreshToken: 0,
  sessionPreviewUrl: null,
  setSessionPreviewUrl: () => undefined,
  suppressHostLogo: false,
  setSuppressHostLogo: () => undefined
};

const HostCompanyLogoSessionContext = createContext<HostCompanyLogoSessionValue>(defaultSession);

export function HostCompanyLogoSessionProvider({
  refreshToken,
  sessionPreviewUrl,
  setSessionPreviewUrl,
  suppressHostLogo,
  setSuppressHostLogo,
  children
}: React.PropsWithChildren<{
  refreshToken: number;
  sessionPreviewUrl: string | null;
  setSessionPreviewUrl: (url: string | null) => void;
  suppressHostLogo: boolean;
  setSuppressHostLogo: (suppress: boolean) => void;
}>) {
  const value = useMemo(
    (): HostCompanyLogoSessionValue => ({
      refreshToken,
      sessionPreviewUrl,
      setSessionPreviewUrl,
      suppressHostLogo,
      setSuppressHostLogo
    }),
    [refreshToken, sessionPreviewUrl, setSessionPreviewUrl, setSuppressHostLogo, suppressHostLogo]
  );

  return <HostCompanyLogoSessionContext.Provider value={value}>{children}</HostCompanyLogoSessionContext.Provider>;
}

export function useHostCompanyLogoSession(): HostCompanyLogoSessionValue {
  return useContext(HostCompanyLogoSessionContext);
}

/** Wraps host upload to bump refresh token after success. */
export function useHostCompanyLogoSessionState() {
  const [refreshToken, setRefreshToken] = useState(0);
  const [sessionPreviewUrl, setSessionPreviewUrlState] = useState<string | null>(null);
  const [suppressHostLogo, setSuppressHostLogo] = useState(false);

  const setSessionPreviewUrl = useCallback((url: string | null) => {
    setSessionPreviewUrlState(url?.trim() ? url : null);
  }, []);

  const bumpRefreshToken = useCallback(() => {
    setRefreshToken((token) => token + 1);
  }, []);

  return {
    refreshToken,
    sessionPreviewUrl,
    setSessionPreviewUrl,
    suppressHostLogo,
    setSuppressHostLogo,
    bumpRefreshToken
  };
}
